<template>
  <ak-sidebar-layout size="large">
    <template #sidebar>
      <ak-box class="h-full is-scrollable">
        <ak-form ref="detailsForm" v-model="isValid">
          <h3 class="color-grey">Set up template</h3>
          <ak-email-templates-details-form
            :active-account="activeAccount"
            :active-agency="activeAgency"
            :template="template"
            :users="users"
            @update-template="onUpdateTemplate"
          />
        </ak-form>
      </ak-box>
    </template>
    <template #body>
      <ak-basic-container>
        <ak-box size="large" class="h-full relative is-scrollable pt">
          <ak-loading
            v-model:active="templatesLoading"
            :can-cancel="false"
            :is-full-page="false"
          />
          <div v-if="!templatesLoading" class="pb">
            <h3 class="color-grey mb">
              Select a premade template or create your own
            </h3>
            <ak-cards>
              <ak-cards-item class="one-half desk--one-third">
                <ak-card
                  hoverable
                  :value="emptyTemplate"
                  :class="{ 'cursor-pointer': isValid }"
                  @click="confirmTemplate(emptyTemplate)"
                >
                  <ak-card-body>
                    <ak-empty-state
                      class="pt"
                      icon="editor-banner"
                      title="Create your own"
                    />
                  </ak-card-body>
                </ak-card>
              </ak-cards-item>
            </ak-cards>
            <div
              v-for="(group, category) in templateGroups"
              :key="category"
              class="mt"
            >
              <h4 class="color-grey-light mb-small">{{ category }}</h4>
              <ak-cards>
                <ak-cards-item
                  v-for="(tmpl, index) of group"
                  :key="index"
                  class="one-half desk--one-third"
                >
                  <ak-card
                    hoverable
                    :class="{ 'cursor-pointer': isValid }"
                    :value="tmpl"
                    @click="confirmTemplate(tmpl)"
                  >
                    <ak-card-image
                      :tag="'img'"
                      :src="tmpl.preview"
                    ></ak-card-image>
                    <ak-card-body size="small">
                      <p class="text-bold color-action mb-none text-center">
                        {{ tmpl.title }}
                      </p>
                    </ak-card-body>
                  </ak-card>
                </ak-cards-item>
              </ak-cards>
            </div>
          </div>
        </ak-box>
      </ak-basic-container>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { ref, computed, onBeforeMount, defineComponent } from 'vue'
import BasicContainer from '../containers/BasicContainer.vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import EmailTemplatesDetailsForm from './EmailTemplatesDetailsForm.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Cards from '@akerolabs/akero-vue/src/components/cards/Cards.vue'
import CardsItem from '@akerolabs/akero-vue/src/components/cards/CardsItem.vue'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import Notification from '@akerolabs/akero-vue/src/components/notification/Notification.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { useRouter } from '../../legacy/router'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-templates-new-template></ak-email-templates-new-template>
   */
  name: 'AkEmailTemplatesNewTemplate',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [SidebarLayout.name]: SidebarLayout,
    [BasicContainer.name]: BasicContainer,
    [EmailTemplatesDetailsForm.name]: EmailTemplatesDetailsForm,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Select.name]: Select,
    [Checkbox.name]: Checkbox,
    [Box.name]: Box,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [Cards.name]: Cards,
    [CardsItem.name]: CardsItem,
    [Card.name]: Card,
    [CardBody.name]: CardBody,
    [EmptyState.name]: EmptyState,
    [Notification.name]: Notification,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Function}
     * @description Checks if a feature is available for the current account
     */
    featureEnabled: {
      default: undefined,
      type: Function,
    },
  },

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const router = useRouter()

    const templatesLoading = ref(true)
    const baseTemplates = ref([])
    const selectedTemplate = ref(null)
    const users = ref([])
    const detailsForm = ref(null)
    const isValid = ref(false)

    const emptyTemplate = ref({
      title: 'Create your own',
      settings: {
        background: '#f3f3f3',
        emailBackground: 'rgba(255,255,255,0)',
        headingsFont: 'Arial, Helvetica, sans-serif',
        bodyFont: 'Arial, Helvetica, sans-serif',
      },
      blocks: [],
    })

    const template = ref({
      version: 2,
      title: '',
      blocks: [],
      settings: {},
      type: 'email',
      style: 'custom',
      from: '',
      replyTo: '',
      subject: '',
      folder: '',
      opts: {
        linkTracking: true,
        signature: {
          enabled: false,
        },
      },
    })

    const templateGroups = computed(() => {
      const result = {}
      baseTemplates.value.forEach((tmpl) => {
        if (!result[tmpl['category']]) {
          result[tmpl['category']] = []
        }
        result[tmpl['category']].push(tmpl)
      })
      return result
    })

    const onUpdateTemplate = (data) => {
      template.value = data
    }

    /**
     * @returns {Promise}
     * @description Fetches the users from the API
     */
    const fetchUsers = () => {
      return request(`/users/`, {
        params: {
          agency: props.activeAgency._id,
          account: props.activeAccount._id,
          overrideAccess: true,
          status: 'active',
          per_page: 0,
        },
      })
    }

    /**
     * @returns {Promise}
     * @description Fetches the base templates from the API
     */
    const fetchBaseTemplates = () => {
      return request(`/email-layouts`, {
        params: {
          agency: props.activeAgency._id,
          account: props.activeAccount._id,
          overrideAccess: true,
          per_page: 0,
        },
      })
    }

    /**
     * @param {Object} layout
     * @description Confirm method for when the user selects a template
     * to use this will fire a confirmation dialog to ask if they are
     * sure they want to use that one.
     */
    const confirmTemplate = (layout) => {
      if (detailsForm.value.validate(true)) {
        createTemplate(layout)
      } else {
        ToastProgrammatic.open({
          message: 'Some fields are incomplete',
          type: 'danger',
        })
      }
    }

    /**
     * @param {Object} layout
     * @description Callback for the confirm dialog, this will make a request
     * to the API and then redirect the user into the edit page, so they can
     * add content to the email template
     */
    const createTemplate = (layout) => {
      templatesLoading.value = true // force the loader so it looks like its working

      const newTemplate = { ...template.value }
      newTemplate.blocks = [...layout.blocks]
      newTemplate.settings = { ...layout.settings }
      newTemplate.account = props.activeAccount._id
      newTemplate.agency = props.activeAgency._id

      request
        .post('/templates', newTemplate)
        .then((response) => {
          ToastProgrammatic.open({
            message: 'Template has been created',
            type: 'success',
          })
          router.push('emailsEdit', { id: response.data.data._id })
        })
        .catch(() => {
          templatesLoading.value.false = false
          ToastProgrammatic.open({
            message: 'Error template could not be created',
            type: 'danger',
          })
        })
    }

    onBeforeMount(async () => {
      const userArray = await fetchUsers()
      const templatesArray = await fetchBaseTemplates()
      baseTemplates.value = templatesArray.data.data
      users.value = userArray.data.data
      templatesLoading.value = false
    })

    return {
      templatesLoading,
      baseTemplates,
      selectedTemplate,
      users,
      emptyTemplate,
      template,
      templateGroups,
      fetchUsers,
      fetchBaseTemplates,
      onUpdateTemplate,
      confirmTemplate,
      detailsForm,
      isValid,
    }
  },
})
</script>
